import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { JsonLd } from "react-schemaorg";
import type { NewsMediaOrganization, VideoObject } from "schema-dts";

import { config } from "shared/data";
import { useCanonicalUrl } from "shared/hooks";
import { formatInTimeZone, getAssetPrefix, notEmpty } from "shared/lib/utils";

import { ISO8601DateTimeFormat } from "scmp-app/components/article/article-schema/consts";
import {
  getVideoDescription,
  getVideoYoutubeEmbedUrl,
  getVideoYoutubeId,
  getVideoYoutubeThumbnailUrl,
  parseDuration,
} from "scmp-app/components/video/helpers";
import type { videoSchemaVideo$key } from "scmp-app/queries/__generated__/videoSchemaVideo.graphql";

type Props = {
  reference: videoSchemaVideo$key;
};

export const VideoSchema: FunctionComponent<Props> = ({ reference }) => {
  const video = useFragment(
    graphql`
      fragment videoSchemaVideo on Video {
        duration
        headline
        publishedDate
        urlAlias
        summary {
          text
        }
        subHeadline {
          text
        }
        image(filter: { type: MAIN_IMAGE }) {
          style(filter: { style: "wide_landscape" }) {
            url
          }
        }
        ...helpersGetVideoYoutubeIdVideo
      }
    `,
    reference,
  );

  const { canonicalUrl } = useCanonicalUrl(video.urlAlias);

  const youtubeId = useMemo(() => getVideoYoutubeId(video), [video]);

  const description = getVideoDescription({
    subHeadline: video.subHeadline?.text,
    summary: video.summary?.text,
  });

  const thumbnailUrl = useMemo(
    () =>
      [video.image?.style?.url, ...(getVideoYoutubeThumbnailUrl(youtubeId) ?? [])].filter(notEmpty),
    [video.image?.style?.url, youtubeId],
  );

  const publisher = useMemo<NewsMediaOrganization>(
    () => ({
      "@type": "NewsMediaOrganization",
      logo: {
        "@type": "ImageObject",
        height: {
          "@type": "QuantitativeValue",
          value: 512,
        },
        url: `${getAssetPrefix()}/icons/scmp-icon-512x512.png`,
        width: {
          "@type": "QuantitativeValue",
          value: 512,
        },
      },
      name: "South China Morning Post",
      url: config.general.canonicalBaseUrl,
    }),
    [],
  );

  if (!notEmpty(video.headline) || thumbnailUrl.length === 0 || !video.publishedDate) return null;

  return (
    <JsonLd<VideoObject>
      item={{
        "@context": "https://schema.org",
        "@id": canonicalUrl,
        "@type": "VideoObject",
        description,
        duration: parseDuration(video.duration),
        embedUrl: getVideoYoutubeEmbedUrl(youtubeId),
        name: video.headline,
        publisher,
        thumbnailUrl,
        uploadDate: formatInTimeZone(video.publishedDate, ISO8601DateTimeFormat),
        url: canonicalUrl,
      }}
    />
  );
};

VideoSchema.displayName = "VideoSchema";
